import { message } from 'antd';
import * as Sentry from '@sentry/react';
import { getStorageItem, removeStorageItem, saveStorageItem } from 'common/helpers/axios.helper';
import { EStorageKey } from 'common/const/axios.enum';
import { ECommonErrorMessage } from 'common/const/axios.enum';
import { ACTIVATION_STEP_THREE, ACTIVATION_STEP_TWO } from 'common/config';
import { store } from 'app/store';
import { IAuth, IToken, refreshTokenExpiration } from 'entities/Auth/Auth.models';
import { setAuth, setIsAuthLoading, setUserCatalogId, setXBasketId, setXUserId } from 'entities/Auth/Auth.slice';

export const saveCreds = async (response: IAuth) => {
  const xUserId = getStorageItem<string>(EStorageKey.XUserId);
  const now = Math.round(Date.now() / 1000);

  const creds = {
    access: {
      id: response.id,
      token: response.accessToken,
      userId: response.id,
      issuedAt: now,
      expiredAt: response.expiresIn ? now + Number(response.expiresIn) : 0,
      accountId: response.accountId,
      accountName: response.accountName,
      firstName: response.firstName,
      lastName: response.lastName,
      patronymic: response.patronymic,
      roles: response.roles,
      isSeller: response.isSeller,
      isSelfRegistered: response.isSelfRegistered,
      selfRegistrationCompletedAt: response.selfRegistrationCompletedAt,
      selfRegistrationStep: response.selfRegistrationStep,
      basketId: response.basketId,
      permissions: response.permissions,
    },
    refresh: {
      id: response.id,
      token: response.refreshToken,
      userId: response.id,
      issuedAt: now,
      expiredAt: now + refreshTokenExpiration,
    },
  };

  if (xUserId) {
    // If xUserId is present, it means that the super administrator is currently in another user’s account.
    const userCredsFromStorage = getStorageItem<IToken>(EStorageKey.Creds);

    if (userCredsFromStorage) {
      /*
      Access/Refresh token, issuedAt, expiredAt - from super admin creds
      */
      const userCreds = {
        access: {
          ...creds.access,
          id: userCredsFromStorage.access.id,
          userId: userCredsFromStorage.access.userId,
          accountId: userCredsFromStorage.access.accountId,
          accountName: userCredsFromStorage.access.accountName,
          firstName: userCredsFromStorage.access.firstName,
          lastName: userCredsFromStorage.access.lastName,
          patronymic: userCredsFromStorage.access.patronymic,
          roles: userCredsFromStorage.access.roles,
          isSeller: userCredsFromStorage.access.isSeller,
          basketId: userCredsFromStorage.access.basketId,
          permissions: userCredsFromStorage.access.permissions,
        },
        refresh: {
          ...creds.refresh,
          id: userCredsFromStorage.refresh.id,
          userId: userCredsFromStorage.refresh.userId,
        },
      };

      store.dispatch(setAuth(userCreds));
      saveStorageItem(EStorageKey.Creds, userCreds);
      saveStorageItem(EStorageKey.SuperAdminCreds, creds);
    } else {
      message.error(ECommonErrorMessage.UnexpectedError);
      Sentry.captureMessage('User creds from storage is undefined');
    }
  } else {
    store.dispatch(setAuth(creds));
    saveStorageItem(EStorageKey.Creds, creds);
  }
};

export const getDeviceId = (): string => {
  const existingDeviceId = getStorageItem<string>(EStorageKey.DeviceId);

  if (existingDeviceId) {
    return existingDeviceId;
  }

  const deviceId = crypto.randomUUID();
  saveStorageItem(EStorageKey.DeviceId, deviceId);

  return deviceId;
};

export const clearCreds = () => {
  removeStorageItem(EStorageKey.Creds);
  removeStorageItem(EStorageKey.XUserId);
  removeStorageItem(EStorageKey.XBasketId);
  removeStorageItem(EStorageKey.UserCatalogId);
  removeStorageItem(EStorageKey.SuperAdminCreds);

  store.dispatch(setAuth(null));
  store.dispatch(setIsAuthLoading(false));
  store.dispatch(setXUserId(null));
  store.dispatch(setXBasketId(null));
  store.dispatch(setUserCatalogId(null));
};

export const getActivationProgressPercent = (step: number) => {
  switch (step) {
    case ACTIVATION_STEP_THREE: {
      return 66;
    }
    case ACTIVATION_STEP_TWO: {
      return 33;
    }
    default: {
      return 0;
    }
  }
};
